<template>
  <ProjectSection
    id="quant"
    title="Quant Engine"
    tagline="A research and simulation engine that tests trading strategies at GPU speed."
    chip="C++17 · CUDA · Node.js"
    accent="#f59e0b"
    :icon="['fas', 'microchip']"
  >
    <!-- Speedup -->
    <article v-reveal class="card span-8 speed-card">
      <h3>From 100K to 15 billion combos a second</h3>
      <p>A grid-search back-tester for intraday index futures and options, rebuilt for Nvidia GPUs with bitset filtering, lookup-table trade jumps and multi-stream pipelines.</p>
      <div class="speed visual">
        <div class="speed-x gradient-text">~100,000×</div>
        <div class="bar-row">
          <span class="bar-label">Prototype</span>
          <span class="bar"><span class="fill slow"></span></span>
          <span class="mono bar-val">100K/s</span>
        </div>
        <div class="bar-row">
          <span class="bar-label">CUDA engine</span>
          <span class="bar"><span class="fill fast"></span></span>
          <span class="mono bar-val hot">15B+/s</span>
        </div>
      </div>
    </article>

    <!-- GPU gauge -->
    <article v-reveal="80" class="card span-4 gauge-card">
      <h3>Never idle</h3>
      <p>Raw multi-exchange market data became typed binary buffers: about 6,000× faster to process, keeping the GPU at 100%.</p>
      <div class="gauge visual visual-center">
        <svg viewBox="0 0 200 200">
          <circle cx="100" cy="100" r="80" class="track" />
          <circle cx="100" cy="100" r="80" class="value" />
        </svg>
        <div class="gauge-label">
          <strong>100%</strong>
          <small>GPU utilization</small>
        </div>
      </div>
    </article>

    <!-- Heatmap -->
    <article v-reveal class="card span-7 heat-card">
      <h3>Sweeping huge parameter grids</h3>
      <p>Large parameter sweeps check that a strategy holds up across the whole grid, not just one lucky setting.</p>
      <div class="heat visual">
        <span
          v-for="(v, i) in heat"
          :key="i"
          class="cell"
          :style="{ '--v': v, animationDelay: (i % 23) * 90 + 'ms' }"
        ></span>
      </div>
    </article>

    <!-- Range compiler -->
    <article v-reveal="80" class="card span-5 compiler-card">
      <h3>A compiler for search ranges</h3>
      <p>JSON ranges compile to compact binary, then eligibility and floor gates prune each combo, shrinking the search space ~32,000×.</p>
      <div class="compile visual">
        <pre class="mono json">{
  "stop":   [4, 40, 2],
  "target": [8, 120, 4],
  "entry":  ["09:31", "11:00"]
}</pre>
        <div class="arrow"><font-awesome-icon :icon="['fas', 'arrow-down']" /></div>
        <div class="mono hex">
          <span v-for="(b, i) in hex" :key="i" :class="{ pruned: i % 3 === 1 }">{{ b }}</span>
        </div>
      </div>
    </article>
  </ProjectSection>
</template>

<script>
import ProjectSection from '../ProjectSection.vue';

// Deterministic pseudo-random values so the heatmap looks the same every render.
function seeded(n) {
  let s = 7;
  return Array.from({ length: n }, (_, i) => {
    s = (s * 9301 + 49297) % 233280;
    const noise = s / 233280;
    const col = i % 23;
    const row = Math.floor(i / 23);
    const ridge = Math.exp(-((col - 14) ** 2) / 40 - ((row - 3) ** 2) / 8);
    return Math.min(1, 0.12 + ridge * 0.85 + noise * 0.25).toFixed(2);
  });
}

export default {
  name: 'QuantSection',
  components: { ProjectSection },
  data() {
    return {
      heat: seeded(23 * 8),
      hex: '04 28 02 08 78 04 1F 2B 3C 6E 00 A4 9B 11 F0 07 C2 3D 5A 80'.split(' '),
    };
  },
};
</script>

<style scoped>
/* Speedup */
.speed-card { min-height: 400px; }

.speed {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 30px;
}

.speed-x {
  font-size: clamp(44px, 7vw, 96px);
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1;
  margin-bottom: 22px;
}

.bar-row {
  display: grid;
  grid-template-columns: 110px 1fr 70px;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
}

.bar-label { font-size: 14px; color: var(--text-dim); }

.bar {
  height: 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.fill {
  display: block;
  height: 100%;
  width: 0;
  border-radius: inherit;
  transition: width 1.8s var(--ease) 0.4s;
}

.fill.slow { background: #52525b; }
.fill.fast {
  background: linear-gradient(90deg, #f59e0b, #fde68a);
  box-shadow: 0 0 24px rgba(245, 158, 11, 0.6);
}

.reveal.is-visible .fill.slow { width: 1.2%; }
.reveal.is-visible .fill.fast { width: 100%; }

.bar-val { color: var(--text-dim); text-align: right; }
.bar-val.hot { color: var(--quant); }

/* Gauge */
.gauge {
  position: absolute;
  left: 50%;
  bottom: 26px;
  width: 190px;
  height: 190px;
  transform: translateX(-50%);
}

.gauge svg { width: 100%; height: 100%; transform: rotate(-90deg); }
.gauge circle { fill: none; stroke-width: 14; }
.track { stroke: rgba(255, 255, 255, 0.06); }

.value {
  stroke: var(--quant);
  stroke-linecap: round;
  stroke-dasharray: 503;
  stroke-dashoffset: 503;
  filter: drop-shadow(0 0 8px rgba(245, 158, 11, 0.6));
  transition: stroke-dashoffset 2s var(--ease) 0.4s;
}

.reveal.is-visible .value { stroke-dashoffset: 0; }

.gauge-label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.gauge-label strong { font-size: 38px; font-weight: 700; }
.gauge-label small { font-size: 12px; color: var(--text-dim); }

/* Heatmap */
.heat {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 28px;
  display: grid;
  grid-template-columns: repeat(23, 1fr);
  gap: 4px;
}

.cell {
  aspect-ratio: 1;
  border-radius: 4px;
  background: rgba(245, 158, 11, var(--v));
  animation: pulse 3.2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.35); }
}

/* Compiler */
.compile {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.json {
  width: 100%;
  margin: 0;
  padding: 14px 16px;
  border-radius: 14px;
  background: #121215;
  border: 1px solid var(--line);
  color: #fcd34d;
  font-size: 12px;
  line-height: 1.6;
}

.arrow { color: var(--quant); font-size: 14px; }

.hex {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.hex span {
  padding: 4px 6px;
  border-radius: 6px;
  background: rgba(245, 158, 11, 0.14);
  color: #fde68a;
  font-size: 12px;
}

.hex span.pruned {
  background: rgba(255, 255, 255, 0.04);
  color: var(--text-faint);
  text-decoration: line-through;
}

@media (max-width: 900px) {
  .heat-card { min-height: 360px; }
  .compiler-card { min-height: 440px; }
  .bar-row { grid-template-columns: 90px 1fr 60px; gap: 10px; }
}
</style>
